import { useNavigate } from "react-router-dom";
import {
  Lightning,
  ArrowRight,
  Brain,
  ChartLineUp,
  Users,
  CheckCircle,
} from "@phosphor-icons/react";

export function HeroSection() {
  const navigate = useNavigate();
  return (
    <section className="relative min-h-screen overflow-hidden bg-white dark:bg-slate-950">
      {/* Background layers */}
      <div className="absolute inset-0 tech-grid-bg opacity-10 dark:opacity-40" />
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 h-150 w-150 rounded-full bg-primary-500/10 blur-[120px] animate-pulse-glow" />
      <div className="absolute bottom-0 right-0 h-100 w-100 rounded-full bg-[#3B7AE0]/8 blur-[100px]" />

      {/* Geometric shapes */}
      <div className="absolute top-20 left-10 h-24 w-24 rotate-45 border border-primary-500/10 animate-spin-slow" />
      <div className="absolute top-40 right-20 h-16 w-16 rotate-12 border border-primary-500/15 rounded-lg animate-float delay-300" />
      <div className="absolute bottom-40 left-1/4 h-20 w-20 rotate-[30deg] border border-[#3B7AE0]/10 animate-float delay-700" />

      <div className="relative mx-auto max-w-7xl px-6 flex items-center min-h-screen pt-20 pb-20">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
          {/* Left: Content */}
          <div className="max-w-xl">
            {/* Atlas badge */}
            <div className="animate-spring-down inline-flex items-center gap-2 rounded-full border border-primary-500/30 bg-primary-500/10 px-4 py-1.5 text-sm font-semibold text-primary-400 backdrop-blur-sm">
              <Brain size={16} weight="fill" className="animate-pulse-glow" />
              <span>Now with Atlas AI assistant</span>
              <span className="h-1.5 w-1.5 rounded-full bg-primary-400 animate-pulse-glow" />
            </div>

            <h1 className="mt-6 text-4xl font-extrabold leading-[1.1] tracking-tight text-slate-900 dark:text-white sm:text-5xl lg:text-6xl animate-spring-up">
              Task management <span className="gradient-text">reimagined</span>{" "}
              for large teams
            </h1>

            <p className="mt-6 text-lg leading-relaxed text-slate-600 dark:text-slate-400 animate-spring-up delay-200">
              Opero gives your team one workspace for tasks, projects, and
              boards — with Atlas, an AI assistant that helps you create and
              track work in plain English. Stop managing. Start shipping.
            </p>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row animate-spring-up delay-300">
              <button
                onClick={() => navigate("/signup")}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 px-8 py-3.5 text-sm font-semibold text-white shadow-xl shadow-primary-500/25 transition-all duration-200 hover:shadow-2xl hover:shadow-primary-500/35 hover:brightness-110"
              >
                Start Free Trial
                <ArrowRight size={16} weight="bold" />
              </button>
              <a
                href="#features"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white/50 dark:bg-slate-800/50 px-8 py-3.5 text-sm font-semibold text-slate-700 dark:text-slate-300 backdrop-blur-sm transition-all duration-200 hover:border-primary-500/50 hover:text-primary-500 dark:hover:text-white"
              >
                See How It Works
              </a>
            </div>

            {/* Social proof */}
            <div className="mt-10 flex flex-wrap items-center gap-6 animate-spring-up delay-500">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <CheckCircle
                  size={16}
                  weight="fill"
                  className="text-primary-500"
                />
                <span>1-month free trial</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <CheckCircle
                  size={16}
                  weight="fill"
                  className="text-primary-500"
                />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <CheckCircle
                  size={16}
                  weight="fill"
                  className="text-primary-500"
                />
                <span>2,400+ teams</span>
              </div>
            </div>
          </div>

          {/* Right: Floating task cards */}
          <div className="relative hidden lg:block">
            {/* Main card */}
            <div className="animate-spring-right delay-200 glass-card rounded-2xl p-6 brand-glow">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Lightning
                    size={18}
                    weight="fill"
                    className="text-primary-400"
                  />
                  <span className="text-sm font-semibold text-slate-900 dark:text-white">
                    Sprint Board
                  </span>
                </div>
                <span className="rounded-full bg-primary-500/20 px-2.5 py-0.5 text-xs font-medium text-primary-300">
                  Live
                </span>
              </div>

              {/* Task rows */}
              {[
                {
                  name: "Design system audit",
                  assignee: "SL",
                  priority: "High",
                  color: "bg-red-500/20 text-red-300",
                },
                {
                  name: "API integration tests",
                  assignee: "KM",
                  priority: "Medium",
                  color: "bg-[#3B7AE0]/20 text-primary-400",
                },
                {
                  name: "Sprint retrospective",
                  assignee: "JT",
                  priority: "Low",
                  color: "bg-emerald-500/20 text-emerald-300",
                },
              ].map((task) => (
                <div
                  key={task.name}
                  className="flex items-center justify-between py-3 border-t border-slate-200/60 dark:border-white/5"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-2 rounded-full bg-primary-400" />
                    <span className="text-sm text-slate-600 dark:text-slate-300">
                      {task.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${task.color}`}
                    >
                      {task.priority}
                    </span>
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 text-xs font-bold text-slate-600 dark:text-slate-300">
                      {task.assignee}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Floating Atlas chat card */}
            <div className="animate-spring-up delay-600 absolute -bottom-8 -left-8 glass-card rounded-xl p-4 animate-float">
              <div className="flex items-center gap-2 mb-2">
                <Brain size={14} weight="fill" className="text-primary-400" />
                <span className="text-xs font-semibold text-primary-300">
                  Atlas
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                &ldquo;Create a task to review the API
                <br />
                integration tests and assign it to Sarah.&rdquo;
              </p>
            </div>

            {/* Floating stats card */}
            <div className="animate-spring-left delay-800 absolute -top-4 -right-4 glass-card rounded-xl p-4 animate-float delay-500">
              <div className="flex items-center gap-2 mb-1">
                <ChartLineUp
                  size={14}
                  weight="bold"
                  className="text-emerald-400"
                />
                <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-300">
                  Team Velocity
                </span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-slate-900 dark:text-white">
                  94
                </span>
                <span className="text-xs text-emerald-400">+12%</span>
              </div>
            </div>

            {/* Floating team card */}
            <div className="animate-spring-right delay-1000 absolute top-1/2 -right-12 glass-card rounded-xl p-3 animate-float delay-300">
              <div className="flex items-center gap-2">
                <Users size={14} weight="fill" className="text-primary-400" />
                <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                  48 active
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white dark:from-slate-950 to-transparent" />
    </section>
  );
}
